/**
 * Push Notification Worker
 * 
 * Client-side worker that polls for pending notifications
 * and triggers push delivery via Edge Function.
 * 
 * Works without pg_net by using client-initiated polling.
 * Only runs for authenticated users with curator/admin roles.
 * 
 * IMPORTANT: Uses getCachedUserId() from AuthContext.
 * DO NOT call supabase.auth.getUser() here — it causes auth loops!
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/services/logger';
import { getCachedUserId } from '@/contexts/AuthContext';

interface PendingNotification {
  id: string;
  user_id: string;
  type: string;
  payload_json: Record<string, unknown> | null;
  created_at: string;
}

// Worker state
let isRunning = false;
let pollInterval: number | null = null;
let lastProcessedAt: string | null = null;

// Configuration
const POLL_INTERVAL_MS = 30_000; // 30 seconds
const BATCH_SIZE = 10;

/**
 * Check if current user should run the worker
 * Only curators and admins run the worker to avoid duplicate sends
 * 
 * Uses cached auth — no getUser() call!
 */
async function shouldRunWorker(): Promise<boolean> {
  try {
    const userId = getCachedUserId();
    if (!userId) return false;

    // Check if user has curator or admin role
    const { data: userRoles } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .in('role', ['curator', 'admin', 'moderator']);

    return (userRoles?.length || 0) > 0;
  } catch {
    return false;
  }
}

/**
 * Get pending notifications that need push delivery
 */
async function getPendingNotifications(): Promise<PendingNotification[]> {
  try {
    // Get notifications created in the last 5 minutes without delivery attempts
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
    const query = supabase
      .from('notifications')
      .select(`
        id,
        user_id,
        type,
        payload_json,
        created_at
      `)
      .gte('created_at', lastProcessedAt || fiveMinutesAgo)
      .is('read_at', null)
      .order('created_at', { ascending: true })
      .limit(BATCH_SIZE);

    const { data, error } = await query;

    if (error) {
      logger.warn('Failed to fetch pending notifications', { 
        error, 
        context: { feature: 'push-worker' } 
      });
      return [];
    }

    return (data || []) as PendingNotification[];
  } catch (err) {
    logger.error('Error fetching pending notifications', { 
      error: err, 
      context: { feature: 'push-worker' } 
    });
    return [];
  }
}

/**
 * Check if user has web push subscription
 */
async function hasWebPushSubscription(userId: string): Promise<boolean> {
  const { data } = await supabase
    .from('user_channel_endpoints')
    .select('id')
    .eq('user_id', userId)
    .eq('channel', 'web_push')
    .eq('is_verified', true)
    .is('deleted_at', null)
    .limit(1)
    .maybeSingle();

  return !!data;
}

// REMOVED: hasDeliveryAttempt() - Edge Function handles duplicate check server-side

/**
 * Send push notification via Edge Function
 */
async function sendPush(notification: PendingNotification): Promise<boolean> {
  try {
    // Check if user has subscription (Edge Function handles duplicate check)
    if (!(await hasWebPushSubscription(notification.user_id))) {
      return true; // No subscription, skip
    }

    const payload = notification.payload_json || {};
    
    const { error } = await supabase.functions.invoke('webpush-send', {
      body: {
        user_id: notification.user_id,
        notification_id: notification.id,
        title: payload.title || 'PetHub',
        body: payload.body || 'Новое уведомление',
        action_url: payload.action_url,
        type: notification.type,
      },
    });

    if (error) {
      logger.warn('Push send failed', { 
        data: { notificationId: notification.id },
        context: { feature: 'push-worker' } 
      });
      return false;
    }

    return true;
  } catch (err) {
    logger.error('Error sending push', { 
      error: err, 
      data: { notificationId: notification.id },
      context: { feature: 'push-worker' } 
    });
    return false;
  }
}

/**
 * Process pending notifications batch
 */
async function processBatch(): Promise<void> {
  if (!navigator.onLine) return;

  const notifications = await getPendingNotifications();
  
  if (notifications.length === 0) return;

  logger.info('Processing push batch', { 
    data: { count: notifications.length },
    context: { feature: 'push-worker' } 
  });

  // Process in parallel with concurrency limit
  const results = await Promise.allSettled(
    notifications.map(n => sendPush(n))
  );

  // Update last processed timestamp
  const lastNotification = notifications[notifications.length - 1];
  if (lastNotification) {
    lastProcessedAt = lastNotification.created_at;
  }

  const succeeded = results.filter(r => r.status === 'fulfilled' && r.value).length;
  const failed = results.length - succeeded;

  if (failed > 0) {
    logger.warn('Some push notifications failed', { 
      data: { succeeded, failed },
      context: { feature: 'push-worker' } 
    });
  }
}

/**
 * Start the push notification worker
 */
export async function startPushWorker(): Promise<void> {
  if (isRunning) return;

  // Only run for curators/admins
  const shouldRun = await shouldRunWorker();
  if (!shouldRun) {
    logger.debug('Push worker not started - user is not curator/admin', {
      context: { feature: 'push-worker' }
    });
    return;
  }

  isRunning = true;
  logger.info('Push notification worker started', {
    context: { feature: 'push-worker' }
  });

  // Initial batch
  await processBatch();

  // Start polling
  pollInterval = window.setInterval(async () => {
    if (!isRunning) return;
    await processBatch();
  }, POLL_INTERVAL_MS);
}

/**
 * Stop the push notification worker
 */
export function stopPushWorker(): void {
  if (!isRunning) return;

  isRunning = false;
  
  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }

  logger.info('Push notification worker stopped', {
    context: { feature: 'push-worker' }
  });
}

/**
 * Check if worker is running
 */
export function isPushWorkerRunning(): boolean {
  return isRunning;
}
